import { PriceList } from '@boltenergy-be/shared'
import dayjs from 'dayjs'
import { SelectedPriceLists } from 'store/contact/types.ts'
import { Attestation } from 'types/contacts.ts'

/**
 * Calculate the price lists based on most recent old price list
 * @param {PriceList[] | undefined} priceLists
 * @returns {SelectedPriceLists | undefined}
 */
export const getPriceListsForEnergyType = (priceLists: PriceList[] | undefined): SelectedPriceLists | undefined => {
  if (!priceLists || priceLists.length === 0) return undefined

  const now = dayjs()
  const breakdown: SelectedPriceLists = {
    current: undefined,
    future: [],
    previous: []
  }

  for (const priceList of priceLists) {
    const fromDate = priceList.fromDate ? dayjs(priceList.fromDate) : null
    const toDate = priceList.toDate ? dayjs(priceList.toDate) : null

    const isAfterFrom = fromDate && now.isSameOrAfter(fromDate) // !! from date is inclusive today
    const isBeforeTo = !toDate || now.isBefore(toDate) // !! to date is exclusive today

    if (isAfterFrom && isBeforeTo) {
      // Determine current price list
      // - From date in the past or today
      // - To date in the future or no to date
      breakdown.current = priceList
    } else if (fromDate && now.isBefore(fromDate)) {
      // Determine future price lists
      // - From date in the future
      breakdown.future.push(priceList)
    } else if (toDate && now.isSameOrAfter(toDate)) {
      // Determine previous price lists
      // - To date in the past or today
      breakdown?.previous.push(priceList)
    }
  }

  // Sort non-empty previous price lists by fromDate descending (newest first)
  if (breakdown.previous.length > 1) {
    breakdown.previous.sort((a, b) => dayjs(b.fromDate).diff(dayjs(a.fromDate)))
  }

  // Sort non-empty previous price lists by fromDate descending (furthest in the future first)
  if (breakdown.future.length > 1) {
    breakdown.future.sort((a, b) => dayjs(b.fromDate).diff(dayjs(a.fromDate)))
  }

  return breakdown
}

/**
 * Find the active social tariff attestation for a given service contract
 * @param {Attestation[] | undefined} attestations
 * @param {string | undefined} ean - Without a given EAN, we will return the first active attestation we find
 * @returns {Attestation | undefined}
 */
export const getActiveSocialTariffAttestation = (
  attestations: Attestation[] | undefined,
  ean: string | undefined
): Attestation | undefined => {
  if (!attestations?.length) {
    return undefined
  }

  const today = dayjs()
  return attestations.find(
    (attestation) =>
      (ean ? attestation.ean === ean : true) && today.isBetween(dayjs(attestation.fromDate), dayjs(attestation.toDate), 'day', '[]')
  )
}
